import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../hooks/use-auth';
import { useToasts } from '../../hooks/use-toasts';
import { isValidEmail, isValidPassword } from '../../utils/validators';

export const useLogin = () => {
  const [values, setValues] = useState<{
    email: string;
    password: string;
    rememberMe: boolean;
  }>({
    email: '',
    password: '',
    rememberMe: true,
  });
  const [errors, setErrors] = useState<{
    email: string | boolean;
    password: string | boolean;
  }>({
    email: false,
    password: false,
  });
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { login, updatePersistence } = useAuth();
  const { showToast } = useToasts();

  const onChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setValues({ ...values, [name]: value });
  };

  const onToggleRememberMe = () => {
    setValues({ ...values, rememberMe: !values.rememberMe });
  };

  useEffect(() => {
    if (hasSubmitted) validateEmail();
  }, [values.email]);

  useEffect(() => {
    if (hasSubmitted) validatePassword();
  }, [values.password]);

  const validateEmail = () => {
    let error: boolean | string = false;
    if (!isValidEmail(values.email)) error = 'Invalid email.';
    setErrors(errors => ({ ...errors, email: error }));
  };

  const validatePassword = () => {
    let error: boolean | string = false;
    if (!isValidPassword(values.password)) error = 'Must be 6 or more characters.';
    setErrors(errors => ({ ...errors, password: error }));
  };

  const onSubmit = async () => {
    setHasSubmitted(true);
    validateEmail();
    validatePassword();
    if (isValidEmail(values.email) && isValidPassword(values.password)) {
      setLoading(true);
      try {
        await updatePersistence(values.rememberMe);
        await login(values.email, values.password);
        navigate('/maps');
      } catch (error: any) {
        if (!error) error = { message: 'An unknown error occured.' };
        switch (error.code) {
          case 'auth/user-not-found':
            // show error on the email field
            setErrors({ ...errors, email: error.message });
            break;
          case 'auth/wrong-password':
            // show error on the password field
            setErrors({ ...errors, password: error.message });
            break;
          default:
            showToast({
              severity: 'error',
              summary: error.message || `An unknown error occured [${error.code}].`,
              detail: `Error code [${error.code || 'unknown'}]`,
              sticky: true,
            });
            break;
        }
      }
      setLoading(false);
    }
  };

  const navigateRegister = () => navigate('/register');

  return {
    values,
    errors,
    loading,
    onSubmit,
    onChange,
    onToggleRememberMe,
    navigateRegister,
  };
};
